import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { redirectAfterSignIn } from "@faire/retailer-visitor-shared/components/AppSignIn/redirectAfterSignIn";
import { AppSignUpForm } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpForm";
import { getModalImageryFromPageResults, getModalImageryType, isAutoSignUpVariant, isFaireDirectVariant } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpModalVariant";
import { useAppSignUpPageResults } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpPageResultsProvider";
import { Disclaimer } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Disclaimer";
import { ImageOrLogo } from "@faire/retailer-visitor-shared/components/AppSignUp/components/ImageOrLogo";
import { MaybeFaireDirectBrandBannerImage } from "@faire/retailer-visitor-shared/components/AppSignUp/components/MaybeFaireDirectBrandBannerImage";
import { SellOnFaire } from "@faire/retailer-visitor-shared/components/AppSignUp/components/SellOnFaire";
import { Subtitle } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Subtitle";
import { Title } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Title";
import { trackFDSignupModal, trackSignupModal } from "@faire/retailer-visitor-shared/components/AppSignUp/eventTracking";
import { LongSignInLink, ShortSignInLink } from "@faire/retailer-visitor-shared/components/AppSignUp/SignInInformation";
import { useAppSignUpState } from "@faire/retailer-visitor-shared/components/AppSignUp/useAppSignUpState";
import { SocialSignOnButtons } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/index";
import { signUpCache } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { showSSOButtons } from "@faire/retailer-visitor-shared/lib/ssoUtils";
import { useAssignSettingFaireDirectSignupEmailAutofill } from "@faire/retailer-visitor-shared/settings/useSettingFaireDirectSignupEmailAutofill";
import { useAssignSettingFaireDirectSignupModalRefresh, useSettingFaireDirectSignupModalRefresh } from "@faire/retailer-visitor-shared/settings/useSettingFaireDirectSignupModalRefresh";
import { useSSOErrorStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOMFAStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOMFAStore";
import { useSSOPasswordStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { useScrollStore } from "@faire/web--source/common/ScrollStore";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { Modal } from "@faire/web--source/slate/Modal/Modal";
import { getSpacing, GrowingSpacer, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackLoggedOutRetailersAutoSignupModalView } from "@faire/web-api--source/events/loggedOutRetailers/view/autoSignupModal";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";
var PAGE_JS_EXECUTED_MARK = markPageJsExecuted();
export var AppSignUp = function() {
    /**
     * Important: it's mandatory to NOT use viewport inside this component (or any of its children).
     * Responsiveness should be handled with css media queries only.
     * Please refer to FD-37046 for more information
     */ var isMobile = useViewport().isMobile;
    var location = useLocation();
    var _useAppSignUpState = useAppSignUpState(), email = _useAppSignUpState.email, signUpParams = _useAppSignUpState.signUpParams, dismissible = _useAppSignUpState.dismissible, isOpenWithFaireSignUp = _useAppSignUpState.isOpenWithFaireSignUp, modalVariant = _useAppSignUpState.modalVariant, handleModalClose = _useAppSignUpState.handleModalClose, handleSubmit = _useAppSignUpState.handleSubmit, setEmailValidationErrors = _useAppSignUpState.setEmailValidationErrors, markUserEntersEmail = _useAppSignUpState.markUserEntersEmail;
    var errorMessage = useSSOErrorStore().errorMessage;
    var userClickedSSOButton = useSSOStore().userClickedSSOButton;
    var assignSettingFaireDirectSignupModalRefresh = useAssignSettingFaireDirectSignupModalRefresh();
    var _useScrollStore = useScrollStore([
        "popNoScroll",
        "pushNoScroll"
    ]), popNoScroll = _useScrollStore.popNoScroll, pushNoScroll = _useScrollStore.pushNoScroll;
    var reactivePageResults = useAppSignUpPageResults();
    var _useState = _sliced_to_array(useState(reactivePageResults), 2), pageResults = _useState[0], setPageResults = _useState[1];
    useEffect(function() {
        // When the page unmounts, data in reactivePageResults will become undefined.
        // To avoid the featuredImage changing unexpectedly and jarring the user,
        // we keep the previous data from pageResults state and only override it
        // if the new data is not undefined.
        setPageResults(function(currentPageResults) {
            return _object_spread({}, currentPageResults, reactivePageResults);
        });
    }, [
        reactivePageResults
    ]);
    var defaultTheme = useTheme();
    useEffect(function() {
        // we want a fresh sign up request when user lands on the homepage
        signUpCache.clear();
    }, []);
    var modalImageryType = getModalImageryType(modalVariant);
    var isFaireDirectSignUp = isFaireDirectVariant(modalVariant);
    var assignFaireDirectSignupEmailAutofill = useAssignSettingFaireDirectSignupEmailAutofill();
    useEffect(function() {
        if (signUpParams.email && isFaireDirectSignUp) {
            assignFaireDirectSignupEmailAutofill();
        }
    }, [
        signUpParams.email,
        assignFaireDirectSignupEmailAutofill,
        isFaireDirectSignUp
    ]);
    var productToken = signUpParams.product || signUpParams.refP;
    var featuredImage = getModalImageryFromPageResults(modalImageryType, pageResults, productToken);
    useEffect(function() {
        if (isFaireDirectVariant(modalVariant)) {
            trackFDSignupModal(location.pathname);
            assignSettingFaireDirectSignupModalRefresh();
        } else {
            trackSignupModal(signUpParams.product, location.pathname);
        }
    }, [
        assignSettingFaireDirectSignupModalRefresh,
        modalVariant,
        location.pathname,
        signUpParams.product
    ]);
    useEffect(function() {
        pushNoScroll();
    }, [
        pushNoScroll
    ]);
    useEffect(function() {
        return function() {
            popNoScroll();
        };
    }, [
        popNoScroll
    ]);
    var loadTimingRef = useUserPerceivedLoadTimingWithKey({
        name: "modal-sign-up",
        pageLoadKey: "static",
        variant: modalVariant,
        isPageLoading: false,
        numComponents: 1,
        pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
        enableTrackPerformanceBreakdown: "internal-tracking"
    });
    var isBrandPreview = getIsBrandPreview();
    var viewRef = useDoActionWhenVisible(function() {
        if (isAutoSignUpVariant(modalVariant)) {
            trackLoggedOutRetailersAutoSignupModalView(location.pathname);
        }
    });
    var shouldShowSellOnFaireBanner = !isMobile && !isBrandPreview && !isFaireDirectSignUp;
    var isFaireDirectSignupRefresh = useSettingFaireDirectSignupModalRefresh();
    var shouldSignInBeAtBottom = isFaireDirectSignUp && !isBrandPreview;
    var ssoButtonSection = showSSOButtons() ? /*#__PURE__*/ _jsx(SSOButtonsSection, {
        isBrandPreview: isBrandPreview,
        isFaireDirectSignUp: isFaireDirectSignUp,
        location: location
    }) : null;
    var textAlignOverride = isFaireDirectSignupRefresh && isMobile ? "left" : undefined;
    return /*#__PURE__*/ _jsxs(StyledModal, {
        setContentRef: loadTimingRef,
        onClose: dismissible ? handleModalClose : undefined,
        height: "auto",
        padding: "0",
        contentStyle: {
            minHeight: "600px",
            alignItems: "stretch",
            width: "500px"
        },
        theme: defaultTheme,
        align: "stretch",
        noPortal: true,
        "data-test-id": "appSignUpModal",
        overlayStyle: {
            /* Use dvh units to prevent overlap with bottom bar on iOS Safari & iOS Chrome */ height: "100dvh"
        },
        children: [
            isFaireDirectSignupRefresh ? /*#__PURE__*/ _jsx(GrowingPictureWrapper, {
                "data-test-id": "faire-direct-brand-banner-image",
                children: /*#__PURE__*/ _jsx(MaybeFaireDirectBrandBannerImage, {})
            }) : /*#__PURE__*/ _jsx(GrowingSpacer, {}),
            /*#__PURE__*/ _jsxs(StyledColumn, {
                ref: viewRef,
                align: "stretch",
                $isFaireDirectRefresh: isFaireDirectSignupRefresh,
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        align: isFaireDirectSignupRefresh ? {
                            mobileAndAbove: "start",
                            tabletAndAbove: "center"
                        } : "center",
                        flex: 1,
                        children: [
                            /*#__PURE__*/ _jsx(ImageOrLogo, {
                                logo: modalImageryType === "no-image",
                                image: featuredImage
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "2x"
                            }),
                            /*#__PURE__*/ _jsx(Title, {
                                modalVariant: modalVariant,
                                align: textAlignOverride
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "1x"
                            }),
                            !isMobile || isFaireDirectSignUp ? /*#__PURE__*/ _jsx(Subtitle, {
                                modalVariant: modalVariant,
                                isOpenWithFaire: isOpenWithFaireSignUp,
                                isFaireMarket: !!signUpParams.faireMarket,
                                livestream: !!signUpParams.livestream,
                                align: textAlignOverride
                            }) : null,
                            userClickedSSOButton && errorMessage ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        height: "2x"
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        color: Core.text.critical,
                                        role: "alert",
                                        align: "center",
                                        children: errorMessage
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        height: "3x"
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(Spacer, {
                                height: "2x"
                            }),
                            /*#__PURE__*/ _jsx(AppSignUpForm, {
                                email: email,
                                handleSubmit: handleSubmit,
                                setEmailValidationErrors: setEmailValidationErrors,
                                markUserEntersEmail: markUserEntersEmail,
                                modalVariant: modalVariant
                            }),
                            shouldSignInBeAtBottom ? null : /*#__PURE__*/ _jsx(ShortSignInLink, {}),
                            ssoButtonSection,
                            shouldSignInBeAtBottom ? /*#__PURE__*/ _jsx(LongSignInLink, {}) : /*#__PURE__*/ _jsx(Spacer, {
                                height: "3x"
                            })
                        ]
                    }),
                    shouldShowSellOnFaireBanner ? /*#__PURE__*/ _jsx(SellOnFaire, {}) : null,
                    /*#__PURE__*/ _jsx(GrowingSpacer, {}),
                    /*#__PURE__*/ _jsx(Disclaimer, {
                        modalVariant: modalVariant,
                        textAlign: "center"
                    })
                ]
            })
        ]
    });
};
var StyledColumn = styled(Column).withConfig({
    componentId: "sc-8a5e2759-0"
})([
    "padding:",
    ";flex:1 1 auto;",
    "{padding:",
    ";}"
], function(param) {
    var $isFaireDirectRefresh = param.$isFaireDirectRefresh;
    return $isFaireDirectRefresh ? getSpacing("3x", "5x", "5x") : getSpacing("5x");
}, responsiveMediaQueries.mobile, getSpacing("3x"));
var SSOButtonsSection = function(props) {
    var isSSR = useIsSSR();
    var location = useLocation();
    var replace = useHistory().replace;
    var _useSSOMFAStore = useSSOMFAStore(), setMFADescription = _useSSOMFAStore.setMFADescription, setMFAOptions = _useSSOMFAStore.setMFAOptions, setMFATitle = _useSSOMFAStore.setMFATitle;
    var setShowConfirmPasswordView = useSSOPasswordStore().setShowConfirmPasswordView;
    return /*#__PURE__*/ _jsx(SocialSignOnButtons, {
        disabled: props.isBrandPreview || isSSR,
        onSignIn: function(user) {
            redirectAfterSignIn(location, user);
        },
        onSelectMFA: function(mfaOptions) {
            setMFAOptions(mfaOptions);
            replace(getCurrentPath().delete("signUp").append({
                signIn: "1"
            }).toString());
        },
        onMFA: function(mfaDescription, mfaTitle) {
            setMFADescription(mfaDescription);
            setMFATitle(mfaTitle);
            replace(getCurrentPath().delete("signUp").append({
                signIn: "1"
            }).toString());
        },
        onConfirmPassword: function() {
            setShowConfirmPasswordView(true);
            replace(getCurrentPath().delete("signUp").append({
                signIn: "1"
            }).toString());
        },
        hideApple: true,
        sourceId: "signup_modal".concat(props.isFaireDirectSignUp ? "_faire_direct" : "")
    });
};
var StyledModal = styled(Modal).withConfig({
    componentId: "sc-8a5e2759-1"
})([
    "",
    "{width:100% !important;height:100% !important;}min-height:0px !important;"
], responsiveMediaQueries.mobile);
var GrowingPictureWrapper = styled(Flex).withConfig({
    componentId: "sc-8a5e2759-2"
})([
    "flex-grow:1;flex-shrink:0;"
]);
